import { APCAcontrast } from './color/APCA';

const reCssColor = /^(?<fn>(?:rgb)a?)\((?<values>.+)\)/;
const colorFnMap = {
    rgb: (r, g, b, a) => ({r, g, b, a}),
    rgba: (r, g, b, a) => ({r, g, b, a})
};

/**
 * Parse number/string color into a standard RGBA object structure
 * @param {number|string} color Number/String representing color
 * (ex: '#fff', 'rgb(255,255,255)', 0xFFFFFF)
 * @returns {object} RGBA structure object { r, g, b, a }
 */
export function parseColor(color) {
    if (typeof color === 'number') {
        // Integer value
        if (isNaN(color) || color < 0 || color > 0xFFFFFF) {
            console.warn(`Invalid hex color '${color}'`);
        }
        return {
            r: (color & 0xFF0000) >> 16,
            g: (color & 0xFF00) >> 8,
            b: (color & 0xFF)
        }
    } else if (typeof color === 'string' && reCssColor.test(color)) {
        // CSS function value
        const groups = color.match(reCssColor).groups;
        const numValues = groups.values.split(/[, ]\s*/).map(parseFloat);
        return colorFnMap[groups.fn](...numValues)
    } else if (typeof color === 'string') {
        // Hexadecimal value
        let hex = color.startsWith('#') ? color.slice(1) : color;
        if (hex.length === 3 || hex.length === 4) {
            hex = hex.split('').map(char => char + char).join('');
        } else if (hex.length !== 6 && hex.length !== 8) {
            console.warn(`Invalid hex color '${color}'`);
        }
        const int = parseInt(hex, 16);
        if (isNaN(int) || int < 0 || int > 0xFFFFFF) {
            console.warn(`Invalid hex color '${color}'`);
        }
        return HexToRGB(hex);
    }
    throw new Error(`Cannot handle color '${color}'. Excepted #hex, #hexa, rgb(), rgba() or number`);
}

/**
 * Transform hexadecimal color representation into RGBA structure object
 * @param {string} hex Hexadecimal color representation
 * @returns {object} RGBA structure object { r, g, b, a }
 */
export function HexToRGB(hex) {
    const parsedHex = parseHex(hex);
    let r = parseInt(parsedHex.substring(0, 2), 16);
    let g = parseInt(parsedHex.substring(2, 4), 16);
    let b = parseInt(parsedHex.substring(4, 6), 16);
    let a = parseInt(parsedHex.substring(6, 8), 16);
    a = (isNaN(a) ? undefined : (a / 255));
    return { r, g, b, a };
}

/**
 * Normalize hexadecimal color representation for parsing
 * @param {string} hex Hexadecimal color representation
 * @returns {string} Normalized hexadecimal color representation (ex: '#cccf' => 'CCCCCCFF')
 */
export function parseHex(hex) {
    if (hex.startsWith('#')) {
        hex = hex.slice(1);
    }

    hex = hex.replace(/([^0-9a-f])/gi, 'F');

    // #rgb or #rgba => #rrggbb or #rrggbbaa
    if (hex.length === 3 || hex.length === 4) {
        hex = hex.split('').map(x => x + x).join('');
    }

    // not #rrggbb or #rggbbaa => complete to #rrggbbaa
    if (hex.length !== 6 && hex.length !== 8) {
        hex = hex.padEnd(6, '0').padEnd(8, 'F');
    }

    return hex;
}

/**
 * Get readable (according to WCAG 3.0) foreground color on a given background color
 * @param {number|string} color Number/String representing a background color
 * (ex: '#fff', 'rgb(255,255,255)', 0xFFFFFF)
 * @returns {number|string} Number/String representing a foreground color
 * (ex: '#fff', 'rgb(255,255,255)', 0xFFFFFF)
 */
export function getForeground(color) {
    const rgbaColor = parseColor(color);
    const blackContrast = Math.abs(APCAcontrast(parseColor(0), rgbaColor));
    const whiteContrast = Math.abs(APCAcontrast(parseColor(0xffffff), rgbaColor));
    return whiteContrast > Math.min(blackContrast, 50) ? '#fff' : '#000';
}
